import React, { useEffect, useState } from "react";
import axios from "axios";
import api, { API_URL, showAlert } from "../util/api";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../header";
import Footer from "../footer";


const ConsultationCompletion = () => {
    /* -----------------------------
       State
    ------------------------------*/

    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [draftSaved, setDraftSaved] = useState(false);
    const router = useNavigate();

    const [form, setForm] = useState({
        diagnosis: { primary: "", secondary: "" },
        clinicalNotes: "",
        prescriptions: [
            { drug: "", dosage: "", frequency: "", duration: "", instructions: "" }
        ],
        outcome: ""
    });
    const { appointment_id } = useParams();

    const draftKey = `consultation_draft_${appointment_id}`;


    /* -----------------------------
       Load Draft on Mount
    ------------------------------*/
    useEffect(() => {
        const savedDraft = localStorage.getItem(draftKey);
        if (savedDraft) {
            setForm(JSON.parse(savedDraft));
        }
    }, [draftKey]);

    /* -----------------------------
       Auto-Save Draft (Debounced)
    ------------------------------*/
    useEffect(() => {
        const timer = setTimeout(() => {
            localStorage.setItem(draftKey, JSON.stringify(form));
            setDraftSaved(true);

            setTimeout(() => setDraftSaved(false), 1500);
        }, 1000);

        return () => clearTimeout(timer);
    }, [form, draftKey]);

    /* -----------------------------
       Helpers
    ------------------------------*/
    const updateField = (path, value) => {
        setForm(prev => {
            const updated = { ...prev };
            let ref = updated;
            const keys = path.split(".");
            keys.slice(0, -1).forEach(k => (ref = ref[k]));
            ref[keys[keys.length - 1]] = value;
            return updated;
        });
    };

    const updatePrescription = (i, field, value) => {
        const copy = [...form.prescriptions];
        copy[i][field] = value;
        setForm({ ...form, prescriptions: copy });
    };

    const addPrescription = () => {
        setForm(prev => ({
            ...prev,
            prescriptions: [
                ...prev.prescriptions,
                { drug: "", dosage: "", frequency: "", duration: "", instructions: "" }
            ]
        }));
    };

    const removePrescription = index => {
        setForm({
            ...form,
            prescriptions: form.prescriptions.filter((_, i) => i !== index)
        });
    };

    /* -----------------------------
       Submit Final
    ------------------------------*/
    const handleSubmit = async () => {
        if (!confirmed || !form.diagnosis.primary || !form.clinicalNotes || !form.outcome) {
            showAlert("error", "error", "Please complete all required fields.");
            return;
        }

        try {
            /* alert(JSON.stringify(form)); */
            setLoading(true);
            let response = await api.post(
                `${API_URL}/consultations/${appointment_id}/complete`,
                form
            );

            if (response.data.status === "success") {
                // Clear draft permanently
                localStorage.removeItem(draftKey);

                showAlert("success", "successful", "Consultation submitted successfully");
               
                router("/appointment/"+appointment_id);



            } else {
                //alert(response.data.message);

                showAlert("error", 'error', response.data.message);
            }


        } catch (error) {
            showAlert("error", "error", "Failed to complete consultation");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchStatus = async () => {

            /* try { */
            const res = await api.get(
                `${API_URL}/consultations/${appointment_id}/check`
            );

            if (res.data.status === "success") {
                setForm(res.data.data);

                localStorage.removeItem(draftKey);
            } 
            /*  } catch (err) {
                 console.error("Failed to fetch consultation status");
             } */
        };

        fetchStatus();
    }, []);


    /* -----------------------------
       UI
    ------------------------------*/
    return (

        <>

            <Navbar menuColor="red" />

            <section style={{ marginTop: "3cm" }}>




                <div className="container my-5">
                    <button
                        onClick={() => router(`/appointment/${appointment_id}`)}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#007bff",
                            cursor: "pointer",
                            fontSize: 16,
                            padding: 0,
                        }}
                    >
                        ← Back
                    </button>
                    <div className="card shadow-lg border-0">
                        <div className="card-header bg-primary text-white">
                            <h4 className="mb-0">🩺 Complete Consultation</h4>
                        </div>

                        <div className="card-body">

                            {/* Draft Indicator */}
                            {draftSaved && (
                                <div className="alert alert-info py-2">
                                    💾 Draft auto-saved
                                </div>
                            )}

                            {/* Diagnosis */}
                            <section className="mb-4">
                                <h5 className="border-bottom pb-2">Diagnosis</h5>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Primary Diagnosis *</label>
                                        <input
                                            className="form-control"
                                            value={form.diagnosis.primary}
                                            onChange={e =>
                                                updateField("diagnosis.primary", e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Secondary Diagnosis</label>
                                        <input
                                            className="form-control"
                                            value={form.diagnosis.secondary}
                                            onChange={e =>
                                                updateField("diagnosis.secondary", e.target.value)
                                            }
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Clinical Notes */}
                            <section className="mb-4">
                                <h5 className="border-bottom pb-2">Clinical Notes *</h5>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    value={form.clinicalNotes}
                                    onChange={e =>
                                        updateField("clinicalNotes", e.target.value)
                                    }
                                />
                            </section>

                            {/* Prescriptions */}
                            <section className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h5>Prescriptions</h5>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={addPrescription}
                                    >
                                        + Add
                                    </button>
                                </div>

                                {form.prescriptions.map((p, i) => (
                                    <div key={i} className="card mb-3">
                                        <div className="card-body">
                                            <div className="row g-3">
                                                <div className="col-md-4">
                                                    <input
                                                        className="form-control"
                                                        placeholder="Drug"
                                                        value={p.drug}
                                                        onChange={e =>
                                                            updatePrescription(i, "drug", e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <input
                                                        className="form-control"
                                                        placeholder="Dosage"
                                                        value={p.dosage}
                                                        onChange={e =>
                                                            updatePrescription(i, "dosage", e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <input
                                                        className="form-control"
                                                        placeholder="Frequency"
                                                        value={p.frequency}
                                                        onChange={e =>
                                                            updatePrescription(i, "frequency", e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <input
                                                        className="form-control"
                                                        placeholder="Duration"
                                                        value={p.duration}
                                                        onChange={e =>
                                                            updatePrescription(i, "duration", e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <textarea
                                                        className="form-control"
                                                        rows="2"
                                                        placeholder="Instructions"
                                                        value={p.instructions}
                                                        onChange={e =>
                                                            updatePrescription(i, "instructions", e.target.value)
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            {form.prescriptions.length > 1 && (
                                                <div className="text-end mt-2">
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => removePrescription(i)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </section>

                            {/* Outcome */}
                            <section className="mb-4">
                                <h5 className="border-bottom pb-2">Consultation Outcome *</h5>
                                <select
                                    className="form-select"
                                    value={form.outcome}
                                    onChange={e =>
                                        updateField("outcome", e.target.value)
                                    }
                                >
                                    <option value="">Select outcome</option>
                                    <option value="RESOLVED">Condition resolved</option>
                                    <option value="OBSERVATION">Under observation</option>
                                    <option value="FOLLOW_UP">Follow-up required</option>
                                    <option value="REFERRED">Referred</option>
                                </select>
                            </section>

                            {/* Confirmation */}
                            <div className="form-check mb-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={confirmed}
                                    onChange={e => setConfirmed(e.target.checked)}
                                />
                                <label className="form-check-label">
                                    I confirm this consultation is complete and final
                                </label>
                            </div>

                            {/* Submit */}
                            <div className="text-end">
                                <button
                                    className="btn btn-success btn-lg"
                                    disabled={loading}
                                    onClick={handleSubmit}
                                >
                                    {loading ? "Completing..." : "Complete Consultation"}
                                </button>
                            </div>

                        </div>
                    </div>
                </div>

            </section>

            <Footer />

        </>
    );
};

export default ConsultationCompletion;
